import type {
  DocId,
  DocKindKey,
  DocPerm,
  DocType,
  MapNode,
  PipelineStage,
  WorkflowState,
} from './types';

/**
 * Documentation slots, types, and THE workflow (V0.38).
 *
 * Three separate concepts that are easy to confuse, so, once:
 *
 *   KIND      which of a node's four fixed SLOTS a page hangs off (plus `custom`, unbounded).
 *             Gated per group by `perm.docButtons`.
 *   TYPE      what the page HOLDS — written here, an embedded URL, or a link out. Chosen by an
 *             admin, never inferred from the URL: v0.21 guessed from the host and mis-read
 *             self-hosted Supademo domains as ordinary links.
 *   WORKFLOW  where one page, in one language, stands: draft | generated | in_review |
 *             approved | needs_update. ONE lifecycle per document, the AI a step inside it.
 *
 * ── THE TWO OLD STATUSES ARE ONE WORKFLOW NOW, DELIBERATELY (V0.38) ───────────────────────
 * This file used to rule that `docStatus` (the AI generation backlog, per node) and
 * `reviewState` (the human review, per page+locale) "must never merge". That mirrored the
 * code's history, not the work — there was always one pipeline per document — and V0.38
 * reversed it on purpose: `Node.docStatus` is DROPPED server-side,
 * `DocPageTranslation.workflowState` is the single truth, and generation lands a page on
 * `generated` through the same mechanical rule that demotes a human edit to `draft`.
 * `server/src/docs/workflow.ts`'s header owns the reversal from the other end.
 *
 * What a NODE shows is the derived `pipelineStage`: two pre-document stages (`no_prompt`,
 * `prompt_ready`) and then the five workflow states of its primary doc's source-locale page.
 * Shipped per node on `/api/map` to that node's prompt scope, never stored.
 */

export interface DocKind {
  key: DocKindKey;
  /** The `perm.docButtons` key that gates this slot. */
  perm: DocPerm;
  label: string;
  /** Button wording when the slot is filled. */
  open: string;
}

export const DOC_KINDS: readonly DocKind[] = [
  { key: 'link', perm: 'documentation', label: 'Documentation', open: 'Open documentation' },
  { key: 'linkInternal', perm: 'internal', label: 'Internal Documentation', open: 'Open internal documentation' },
  { key: 'linkCourse', perm: 'course', label: 'Course', open: 'Open course' },
  { key: 'linkDemo', perm: 'demo', label: 'Demo', open: 'Open demo' },
];

/**
 * Which `perm.docButtons` gate a slot answers to, including the synthetic `custom` one.
 *
 * `custom` is not one of the four fixed slots — it appends to a list rather than filling a
 * field — but it is still a kind as far as creating and searching for pages is concerned, so
 * every caller needs the same mapping. It was written out twice before this existed.
 */
export function docKindForKey(key: DocKindKey | 'custom' | string): DocPerm {
  if (key === 'custom') return 'custom';
  return DOC_KINDS.find((d) => d.key === key)?.perm ?? 'documentation';
}

/** Slot key -> the node field holding its doc id. */
export const DOC_ID_FIELD_BY_KEY = {
  link: 'docId',
  linkInternal: 'internalDocId',
  linkCourse: 'courseDocId',
  linkDemo: 'demoDocId',
} as const satisfies Record<DocKindKey, keyof MapNode>;

/**
 * Display order for the doc rows and the zoomed-out indicator: documentation, course,
 * internal, demo. Deliberately NOT `DOC_KINDS`' own order — that one is the order slots were
 * added; this one is the order they are shown in, and they have drifted apart on purpose.
 */
export const DOC_BEAN_ORDER: readonly DocKindKey[] = ['link', 'linkCourse', 'linkInternal', 'linkDemo'];

/** CSS custom properties, so the four slot colours have one definition (see theme/tokens). */
export const DOC_BEAN_COLOR: Record<DocKindKey, string> = {
  link: 'var(--doc-color)',
  linkCourse: 'var(--course-color)',
  linkInternal: 'var(--internal-color)',
  linkDemo: 'var(--demo-color)',
};

/**
 * The kind's bean colour. `custom` has no bean of its own, so it takes the neutral ink the
 * map's own custom pills use (`features/map/nodeView.ts`).
 */
export function kindColorOf(perm: DocPerm): string {
  const key = DOC_KINDS.find((d) => d.perm === perm)?.key;
  return key ? DOC_BEAN_COLOR[key] : 'var(--ink-faint)';
}

/** The kind's name, for a label. `custom` names itself — it is not one of the four slots. */
export const kindLabelOf = (perm: DocPerm): string =>
  DOC_KINDS.find((d) => d.perm === perm)?.label ?? 'Custom';

/**
 * What kind a document is ON A GIVEN NODE: which of the four slots holds it, or `custom`.
 *
 * A document has no intrinsic kind — the SLOT it is attached to is its kind, and the same
 * page attached to two nodes can be a Course on one and a Demo on the other. That is why this
 * takes the node and not just the id.
 */
export function docPermOnNode(n: MapNode | undefined, docId: DocId): DocPerm {
  if (n) {
    for (const k of DOC_KINDS) if (n[DOC_ID_FIELD_BY_KEY[k.key]] === docId) return k.perm;
  }
  return 'custom';
}

export interface DocTypeDef {
  key: DocType;
  label: string;
  icon: string;
  hint: string;
}

export const DOC_TYPES: readonly DocTypeDef[] = [
  { key: 'standard', label: 'Standard doc', icon: 'i-doc', hint: 'A page you write here, in the editor.' },
  {
    key: 'iframe',
    label: 'Iframe',
    icon: 'i-supademo',
    hint: 'A page embedded inside InfoStation — a Supademo, a dashboard, a live report.',
  },
  { key: 'external', label: 'External link', icon: 'i-ext', hint: 'A link out. Opens in a new browser tab.' },
];

export const docTypeDef = (t: DocType | string | undefined): DocTypeDef =>
  DOC_TYPES.find((d) => d.key === t) ?? DOC_TYPES[0];

/**
 * What a page holds.
 *
 * The server OMITS 'standard' from the `docTypes` map it sends, so absent means standard.
 * That is why this is a lookup with a default and not a field read.
 */
export const docTypeOf = (docTypes: Record<DocId, DocType>, docId: DocId | null | undefined): DocType =>
  (docId && docTypes[docId]) || 'standard';

export const docIconId = (docTypes: Record<DocId, DocType>, docId: DocId | null | undefined): string =>
  docTypeDef(docTypeOf(docTypes, docId)).icon;

/**
 * A page's label on a MAP pill, where the row is 22px and there is no room to waste.
 *
 * An external link's icon already says it leaves the app, so a title that opens by repeating
 * the word — "External: https://…", left over from before the type existed — spends the whole
 * row on nothing. Stripped here and only here: the page's real title is untouched everywhere
 * else, including in the popup and the doc overlay.
 */
export function docPillLabel(
  docId: DocId | null | undefined,
  fallback: string,
  opts: { docTitles: Record<DocId, string>; docTypes: Record<DocId, DocType> },
): string {
  const raw = (docId && opts.docTitles[docId]) || fallback;
  if (docTypeOf(opts.docTypes, docId) !== 'external') return raw;
  const stripped = String(raw)
    .replace(/^\s*external(\s+links?)?\b[\s:\u2013\u2014-]*/i, '')
    .trim();
  return stripped || raw;
}

/* --------------------------------------------------- the pipeline vocabulary (V0.38) */

export interface PipelineStageDef {
  key: PipelineStage;
  label: string;
  color: string;
  /** One sentence saying what standing here MEANS — the tab/tooltip wording. */
  hint: string;
}

/**
 * The SEVEN pipeline stages, in lifecycle order — what replaced `STATUS_DISPLAY` and
 * `effectiveDocStatus` when the two statuses merged (V0.38). Every display, filter and count
 * path reads this list; there is no "stored six" any more because nothing stores a stage.
 *
 * THE COLOURS ARE THE PALETTE THE APP ALREADY HAD, on purpose. The three states the doc bar's
 * chip has always shown — draft, in_review, approved — keep the exact `--review-*` tokens the
 * chip was photographed with, so the closed doc bar is pixel-identical. `generated` takes the
 * blue `doc_generated` wore, the two pre-document stages keep `not_started`'s grey and
 * `prompt_present`'s light blue, and `needs_update` takes the red that `doc_rejected` used to
 * hold — its old amber now belongs to `in_review`, and two stages sharing a colour would make
 * the highlight layer unreadable. `theme/tokens.ts` stays the one definition of the values.
 */
export const PIPELINE_DISPLAY: readonly PipelineStageDef[] = [
  {
    key: 'no_prompt',
    label: 'No prompt',
    color: 'var(--status-not-started)',
    hint: 'No AI prompt and no page yet — nothing has started.',
  },
  {
    key: 'prompt_ready',
    label: 'Prompt ready',
    color: 'var(--status-prompt-present)',
    hint: 'A prompt is written and waiting for generation.',
  },
  {
    key: 'draft',
    label: 'Draft',
    color: 'var(--review-draft)',
    hint: 'Written — the working state nobody has cleared.',
  },
  {
    key: 'generated',
    label: 'Generated',
    color: 'var(--status-doc-generated)',
    hint: 'The AI wrote this page; no person has cleared it yet.',
  },
  {
    key: 'in_review',
    label: 'In review',
    color: 'var(--review-in-review)',
    hint: 'Waiting for a reviewer.',
  },
  {
    key: 'approved',
    label: 'Approved',
    color: 'var(--review-approved)',
    hint: 'A person has read and approved this text.',
  },
  {
    key: 'needs_update',
    label: 'Needs update',
    color: 'var(--status-doc-rejected)',
    hint: 'Flagged as outdated — the page needs a rework.',
  },
];

export const PIPELINE_COLOR: Record<string, string> = Object.fromEntries(
  PIPELINE_DISPLAY.map((s) => [s.key, s.color]),
);
export const PIPELINE_LABEL: Record<string, string> = Object.fromEntries(
  PIPELINE_DISPLAY.map((s) => [s.key, s.label]),
);

/** The five WORKFLOW states are the pipeline minus its two pre-document stages. */
export const WORKFLOW_DISPLAY: readonly PipelineStageDef[] = PIPELINE_DISPLAY.slice(2);

const WORKFLOW_KEYS = new Set<string>(WORKFLOW_DISPLAY.map((s) => s.key));

/**
 * Read a value that came off the wire as a workflow state.
 *
 * `draft` is the fallback rather than a null, because that is what the column defaults to and
 * what a page with no row is treated as everywhere else. Use it only where a state is KNOWN to
 * exist — "this page has no translation in this locale" is a different sentence and the
 * callers that need it check for the entry's absence instead.
 */
export function asWorkflowState(v: unknown): WorkflowState {
  return typeof v === 'string' && WORKFLOW_KEYS.has(v) ? (v as WorkflowState) : 'draft';
}

export interface WorkflowVerbDef {
  /** As `POST /api/docs/:id/review/:verb` spells it — the path segment kept its old name. */
  verb: 'request' | 'approve' | 'reject' | 'flag';
  /** The state the verb moves a page TO. */
  to: WorkflowState;
  /** What the button says. */
  action: string;
}

/**
 * The four verbs, in the order the panel offers them.
 *
 * NOT A STATE MACHINE. Every verb is allowed from any state (V0.36's no-ceremony rule
 * survives the unification): a one-person install must not be forced through ceremony to
 * mark its own page correct. The panel merely hides the verb whose target the page is
 * already at. `draft` is also reached mechanically — a content save demotes to it — and
 * `generated` ONLY mechanically: a generation lands there, no verb does.
 */
export const WORKFLOW_VERBS: readonly WorkflowVerbDef[] = [
  { verb: 'request', to: 'in_review', action: 'Request review' },
  { verb: 'approve', to: 'approved', action: 'Approve' },
  { verb: 'reject', to: 'draft', action: 'Send back to draft' },
  { verb: 'flag', to: 'needs_update', action: 'Needs update' },
];

/**
 * A node's pipeline stage, kept LIVE over local edits.
 *
 * The server derives and ships `pipelineStage` to the node's prompt scope; this re-derives
 * the two pre-document stages from `aiPrompt` with the server's own rule, so typing a prompt
 * into the console moves `no_prompt` -> `prompt_ready` without a round trip — exactly the
 * live behaviour the derived `prompt_present` used to have.
 *
 * `undefined` means "you may not see this": the field is stripped for a viewer outside the
 * node's prompt scope, and inventing a stage for them would be inventing knowledge. Callers
 * gate their UI on that (the stage filter and the console only ever see nodes that have it).
 */
export function pipelineStageOf(
  n: Pick<MapNode, 'pipelineStage' | 'docId' | 'aiPrompt'> | null | undefined,
): PipelineStage | undefined {
  if (!n || n.pipelineStage === undefined) return undefined;
  if (!n.docId) return (n.aiPrompt ?? '').trim() ? 'prompt_ready' : 'no_prompt';
  // A doc just attached locally may still carry a pre-document stage; the server reads a
  // missing translation row as draft, so the mirror does too.
  return WORKFLOW_KEYS.has(n.pipelineStage) ? (n.pipelineStage as WorkflowState) : 'draft';
}

/**
 * The first documentation a node actually shows, in the same order as its pills.
 *
 * Clicking a node has to land on SOMETHING, or the overlay keeps whatever was open before
 * and it reads as this node's documentation, which it never was. Respects the group's
 * `docButtons`, so a user lands on the first slot THEY can see.
 */
export function firstVisibleDocOf(
  n: MapNode,
  canDocBtn: (perm: DocPerm) => boolean,
): { docId: DocId; key: DocKindKey | null } | null {
  for (const key of DOC_BEAN_ORDER) {
    const kind = DOC_KINDS.find((d) => d.key === key)!;
    const docId = n[DOC_ID_FIELD_BY_KEY[key]] as DocId | null | undefined;
    if (canDocBtn(kind.perm) && docId) return { docId, key };
  }
  if (canDocBtn('custom') && (n.customDocs ?? []).length) {
    return { docId: n.customDocs![0], key: null };
  }
  return null;
}

/** Every doc id on a node, in display order, filtered by what this group may see. */
export function visibleDocsOf(
  n: MapNode,
  canDocBtn: (perm: DocPerm) => boolean,
): { docId: DocId; key: DocKindKey | null }[] {
  const out: { docId: DocId; key: DocKindKey | null }[] = [];
  for (const key of DOC_BEAN_ORDER) {
    const kind = DOC_KINDS.find((d) => d.key === key)!;
    const docId = n[DOC_ID_FIELD_BY_KEY[key]] as DocId | null | undefined;
    if (canDocBtn(kind.perm) && docId) out.push({ docId, key });
  }
  if (canDocBtn('custom')) for (const docId of n.customDocs ?? []) out.push({ docId, key: null });
  return out;
}

/**
 * The one place a documentation URL is built.
 *
 * `docUrls` carries the target for an `iframe` or `external` page. A `standard` page has no
 * entry and is served by the renderer, by ID — not by slug, which exists but addresses
 * nothing until routing lands. Every consumer treats the result as an opaque URL.
 */
export function docUrlFor(
  docId: DocId,
  opts: { docUrls: Record<DocId, string>; locale: string },
): string {
  return opts.docUrls[docId] || `/api/docs/${docId}/render?lang=${opts.locale}`;
}

/**
 * The colour a revision SOURCE is drawn in, wherever one is drawn — the Logs table's dot and
 * the dashboard's AI column share this table (V0.44) so a third surface cannot invent a third
 * palette. Server values, so not translated. Human edits are faint and AI writes take the
 * accent: the two halves of the feed's human/AI split.
 */
export const REVISION_SOURCE_COLOR: Record<string, string> = {
  ai_generated: 'var(--accent)',
  ai_translated: '#8b5cf6',
  human_edit: 'var(--ink-faint)',
  restore: 'var(--status-proofread,#0a7)',
  ai_failed: 'var(--status-doc-rejected,#d0483c)',
};
